import React, { useState } from 'react';
import {
  Modal,
  Text,
  Button,
  Group,
  Stack,
  Title,
  Box,
  List,
  ThemeIcon,
  Divider,
  Paper,
  Anchor
} from '@mantine/core';
import { 
  IconFaceId, 
  IconCalendarTime, 
  IconUsersGroup, 
  IconPhotoAi, 
  IconShieldLock, 
  IconSettings 
} from '@tabler/icons-react';

interface AnalysisOptInModalProps {
  opened: boolean;
  onClose: () => void;
  enableFaceAnalysis: boolean;
  onSaveSettings: (settings: { enableFaceAnalysis: boolean }) => Promise<void>;
}

export function AnalysisOptInModal({
  opened,
  onClose,
  enableFaceAnalysis,
  onSaveSettings
}: AnalysisOptInModalProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConsent = async () => {
    try {
      setSaving(true);
      setError(null);
      await onSaveSettings({
        enableFaceAnalysis: true
      });
      onClose();
    } catch (err) {
      setError('Failed to save your preferences. Please try again.');
      console.error('Error saving analysis preferences:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDecline = async () => {
    try {
      setSaving(true);
      setError(null);
      await onSaveSettings({
        enableFaceAnalysis: false
      });
      onClose();
    } catch (err) {
      setError('Failed to save your preferences. Please try again.');
      console.error('Error saving analysis preferences:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Title order={3}>Facial Recognition Consent</Title>}
      size="lg"
      closeOnClickOutside={false}
      closeOnEscape={false}
      withCloseButton={false}
    >
      <Stack gap="md">
        <Text>
          We use facial recognition technology to help organize your photos and enhance your experience. 
          With your consent, we'll analyze faces in your images to provide these helpful features:
        </Text>
        
        <Paper p="md" withBorder>
          <List spacing="md">
            <List.Item
              icon={
                <ThemeIcon color="blue" size={32} radius="xl">
                  <IconFaceId size={18} />
                </ThemeIcon>
              }
            >
              <Text fw={500}>Tag and identify people in your photos</Text>
            </List.Item>
            
            <List.Item
              icon={
                <ThemeIcon color="blue" size={32} radius="xl">
                  <IconCalendarTime size={18} />
                </ThemeIcon>
              }
            >
              <Text fw={500}>Predict dates for events more accurately based on subject age</Text>
            </List.Item>
            
            <List.Item
              icon={
                <ThemeIcon color="blue" size={32} radius="xl">
                  <IconUsersGroup size={18} />
                </ThemeIcon>
              }
            >
              <Text fw={500}>Improve grouping suggestions based on who appears in each image</Text>
            </List.Item>
            
            <List.Item
              icon={
                <ThemeIcon color="blue" size={32} radius="xl">
                  <IconPhotoAi size={18} />
                </ThemeIcon>
              }
            >
              <Text fw={500}>Generate content-aware captions based on subjects & family context</Text>
            </List.Item>
          </List>
        </Paper>
        
        <Box>
          <Text fw={600} mb="xs">What we collect:</Text>
          <Text>
            We analyze faces in your photos and associate them with identities you define (such as names). 
            This information is used exclusively within your account to enhance your photo organization experience.
          </Text>
        </Box>
        
        <Box>
          <Text fw={600} mb="xs">Your rights:</Text>
          <List spacing="xs">
            <List.Item
              icon={
                <ThemeIcon color="gray" variant="light" size={24} radius="xl">
                  <IconSettings size={14} />
                </ThemeIcon>
              }
            >
              <Text size="sm">You can use the app without enabling facial recognition, but some features will be limited.</Text>
            </List.Item>
            <List.Item
              icon={
                <ThemeIcon color="gray" variant="light" size={24} radius="xl">
                  <IconSettings size={14} />
                </ThemeIcon>
              }
            >
              <Text size="sm">You may withdraw your consent at any time via the app's settings page.</Text>
            </List.Item>
            <List.Item
              icon={
                <ThemeIcon color="gray" variant="light" size={24} radius="xl">
                  <IconShieldLock size={14} />
                </ThemeIcon>
              }
            >
              <Text size="sm">We do not share facial data with third parties.</Text>
            </List.Item>
          </List>
        </Box>
        
        <Text size="sm">
          By clicking "I Consent", you agree to the processing of facial data as described above and in our{' '}
          <Anchor href="/privacy-policy" target="_blank" fw={500}>
            Privacy Policy
          </Anchor>.
        </Text>

        {error && (
          <Text color="red" fw={500}>
            {error}
          </Text>
        )}

        <Divider my="sm" />

        <Group grow mt="md" px="md">
          <Button 
            variant="outline" 
            onClick={handleDecline} 
            disabled={saving}
            size="md"
            fullWidth
          >
            I Do Not Consent
          </Button>
          <Button 
            color="blue" 
            onClick={handleConsent} 
            disabled={saving}
            size="md"
            fullWidth
          >
            I Consent
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
